import { is, List, OrderedMap } from 'immutable';
import React, { useMemo, useState } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';

import i18n from '@i18n';
import { denormalizeSportEventId } from '@/nikeone/domain/sportevent';
import { DialogBody, DialogTitle, Docked } from '@/ui/dialogs/Dialog';
import immutableMemo from '@/common/immutableMemo';
import BlockLoader from '@/ui/BlockLoader';
import { fireGAEvent } from '@/analytics';
import NotificationSwitch from '@/myprofile/views/settings/NotificationSwitch';
import { subscribeToMatch } from '@/notifications/handlers';
import { matchSubscriptions$, matchNotificationTypes$ } from '@/notifications/subs';

function MatchNotifyDialog({ data }) {
    const eventId = useMemo(() => String(denormalizeSportEventId(data.eventId)), [data.eventId]);
    const derefSub = useReframe();
    const notificationTypes = derefSub(matchNotificationTypes$(eventId));
    if (!notificationTypes) {
        return null;
    }
    return (
        <Docked>
            <DialogTitle>
                <h2>{i18n('notification.settings')}</h2>
            </DialogTitle>
            <DialogBody>
                {notificationTypes.size ? (
                    <MatchesTypesContent eventId={eventId} eventTypes={notificationTypes} />
                ) : (
                    i18n('notifications.match.empty')
                )}
            </DialogBody>
        </Docked>
    );
}

export function MatchNotifyPane({ eventId }) {
    const deEventId = useMemo(() => String(denormalizeSportEventId(eventId)), [eventId]);
    const derefSub = useReframe();
    const notificationTypes = derefSub(matchNotificationTypes$(deEventId));
    if (!notificationTypes) {
        return <BlockLoader />;
    }
    return (
        <div className="tabs-layout-rw">
            <div className="notifications-pane">
                {notificationTypes.size ? (
                    <MatchesTypesContent eventId={deEventId} eventTypes={notificationTypes} />
                ) : (
                    i18n('notifications.match.empty')
                )}
            </div>
        </div>
    );
}

function subscribe(eventId, types) {
    if (!types) {
        return;
    }
    const deEventId = denormalizeSportEventId(eventId).toString();
    const notificationTypes = types.filter(Boolean).keySeq();
    dispatch(subscribeToMatch(deEventId, notificationTypes));
}

const MatchesTypesContent = immutableMemo(function MatchesTypesContent({ eventId, eventTypes }) {
    const { derefSub } = useReframe();
    const match = derefSub(matchSubscriptions$(eventId));
    const matchTypes = match ? match.get('types') : List();
    const mergedTypes = eventTypes.reduce((map, type) => map.set(type, !!matchTypes.includes(type)), OrderedMap());

    // to je take ze ked sa derefne uplne ina hodnota, tak sa nou prepise lokalny stav
    // asi to nie je moc potrebne
    const [types, setTypes] = useState(mergedTypes);
    const [prevMergedTypes, setPrevMergedTypes] = useState(mergedTypes);
    if (!is(mergedTypes, prevMergedTypes)) {
        setPrevMergedTypes(mergedTypes);
        setTypes(mergedTypes);
    }

    function onChange(type, value) {
        const newTypes = types.set(type, value);
        setTypes(newTypes);
        subscribe(eventId, newTypes);
        fireGAEvent('Ponuka', `${type} ${value ? 'ON' : 'OFF'}`, 'Upozornenia');
    }

    function onChangeAll(type, value) {
        const newTypes = types.map(() => value);
        setTypes(newTypes);
        subscribe(eventId, newTypes);
        fireGAEvent('Ponuka', `${type} ${value ? 'ON' : 'OFF'}`, 'Upozornenia');
    }

    return (
        <div>
            <NotificationSwitch
                key="all"
                className="shorten"
                label={i18n('notifications.match.type.all')}
                type="all"
                value={!types.includes(false)}
                onChange={onChangeAll}
            />
            {types.size ? <div className="light-separator" /> : null}
            {types.keySeq().map(type => (
                <NotificationSwitch
                    key={type}
                    className="shorten"
                    label={i18n(`notifications.match.type.${type}`)}
                    type={type}
                    value={types.get(type)}
                    onChange={onChange}
                />
            ))}
        </div>
    );
});

export default MatchNotifyDialog;
